import React, {
    useState,
    useEffect,
    useRef
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Col, Row, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { useStoreState } from 'easy-peasy';
import UploadMediaComponent from "../widgets/uploadMedia/UploadMediaComponent.jsx";
import ButtonLoaderComponent from '../widgets/ButtonLoaderComponent';
import DatePicker from "react-datepicker";
import SelectMultipleItemsComponent from "../widgets/selectMultipleItemsComponent/SelectMultipleItemsComponent.jsx";
import { URLS } from '../../services/axios';
import HelperMetiers360 from '../../services/HelpersMetiers360.js';

const dropzoneOptions = {
    url: URLS.API_URL + "/_uploader/gallery/upload",
    headers: {
        'Cache-Control': null,
        'X-Requested-With': null
    },
    withCredentials: true,
    chunking: true,
    autoProcessQueue: false,
    maxFiles: 1,
    acceptedFiles: ".apk",
    addRemoveLinks: true
};


const ApkFormComponent = ((props) => {
    const { apk, actionFn} = props;

    const {
        apiData: { ACTIONS, BACKOFFICE_DEFAULT_VALUES }
    } = useStoreState(state => state.actionSlugs);

    const {packageById} = useStoreState(state => state.packages);

    const { typeOfHeadset: allHeadsetsTypes } = BACKOFFICE_DEFAULT_VALUES;

    const formRef = useRef(null);
    const [uploadedApk, setUploadedApk] = useState(null);

    const [isSending, setIsSending] = useState(false);
    const [publicationDate, setPublicationDate] = useState(apk?.publicationDate?.date ? new Date(apk.publicationDate.date) : null);
    const [isKioskMode, setIsKioskMode] = useState(false);

    const [formErrors, setFormErrors] = useState({
        apkFile: null,
        publicationDate: null,
        deviceTypes: null,
        loaderConditions: {}
    });


    const [deviceTypes, setDeviceTypes] = useState([]);
   
    const [loaderConditions, setLoaderConditions] = useState([]);

    const packagesSlugs = useStoreState(actions => actions.actionSlugs.actionSlugsDispatcher('packages'));

    const navigate = useNavigate();

    const token = ACTIONS?.apks?.[!apk ? "create" : "update"]?.csrfToken;


    useEffect(() => {
        setLoaderConditions(prev =>
            deviceTypes.map(device => {
                const existing = prev.find(
                    c => c.deviceType === device.reference
                );

                return existing || {
                    deviceType: device.reference,
                    loaderMinVersion: ''
                };
            })
        );
    }, [deviceTypes]);


    useEffect(() => {
        if (apk) {
            setDeviceTypes(apk.headsetTypes || []);
            setLoaderConditions(
                (apk.loaderConditions || []).map(condition => ({
                    ...condition,
                    deviceType: condition.deviceTypeReference
                }))
            );
            setPublicationDate(apk.publicationDate ? new Date(apk.publicationDate.date) : null);
            setIsKioskMode(apk.isKioskMode);
        }
    }, [apk]);

    const handleLoaderMinChange = (deviceType, value) => {
        
        setLoaderConditions(prev =>
            prev.map(condition =>
                condition.deviceType === deviceType
                    ? {
                        ...condition,
                        loaderMinVersion:
                            value === '' ? '' : parseInt(value, 10)
                    }
                    : condition
            )
        );

        setFormErrors(prev => ({
            ...prev,
            loaderConditions: {
                ...prev.loaderConditions,
                [deviceType]: null
            }
        }));
    };

    const validateForm = () => {
        const errors = {
            apkFile: null,
            publicationDate: null,
            deviceTypes: null,
            loaderConditions: {}
        };

        if (!uploadedApk && !apk) {
            errors.apkFile = "Veuillez charger un fichier APK";
        }

        if (!publicationDate) {
            errors.publicationDate = "Veuillez indiquer une date de publication";
        }

        if (!deviceTypes || deviceTypes.length === 0) {
            errors.deviceTypes = "Veuillez sélectionner au moins un type de casque";
        }

        deviceTypes.forEach(device => {
            const condition = loaderConditions.find(
                c => c.deviceType === device.reference
            );

            if (
                !condition ||
                condition.loaderMinVersion === '' ||
                condition.loaderMinVersion === null ||
                isNaN(condition.loaderMinVersion)
            ) {
                errors.loaderConditions[device.reference] =
                    "Version loader minimale requise";
            }
        });

        const hasErrors =
            errors.apkFile ||
            errors.publicationDate ||
            errors.deviceTypes ||
            Object.keys(errors.loaderConditions).length > 0;

        setFormErrors(errors);

        return !hasErrors;
    };

    const submitForm = () => {
        if (!validateForm()) {
            return;
        }

        setIsSending(true);

        const data = new FormData(formRef.current);

        if (uploadedApk) {
            data.append('apk[file]', uploadedApk);
        }

        data.append('apk[loaderConditions]',JSON.stringify(loaderConditions));
        data.append('apk[packageUniqueId]', packageById?.uniqueId || '');
        data.append('apk[publicationDate]', HelperMetiers360.formatDateForFormControl(publicationDate));
        data.append('apk[isKioskMode]', isKioskMode);

        const payload = { data };

        if (apk?.uniqueId) {
            payload.uniqueId = apk.uniqueId;
        }

        actionFn(payload).then(() => {
            navigate(packagesSlugs.readOne.replace(':uniqueId', packageById?.uniqueId))
        }).finally(() => {
            setIsSending(false);
        });
    };


    return (
        <div>
            <Form
                ref={formRef}
                method="post"
                encType="multipart/form-data"
            >
                {/* APK FILE */}
                <Form.Label>Fichier APK</Form.Label>
                <UploadMediaComponent
                    {...dropzoneOptions}
                    setFile={(file) => setUploadedApk(file)}
                />

                {formErrors.apkFile && (
                    <div className="invalid-feedback d-block">
                        {formErrors.apkFile}
                    </div>
                )}

                <br />

                {/* DESCRIPTION */}
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={4}
                    name="apk[description]"
                    defaultValue={apk?.description || ''}
                    placeholder="Description de l'application APK"
                />
                <br />

                {/* PUBLICATION DATE */}
                <Form.Label>Date de publication</Form.Label>
                <DatePicker
                    showIcon
                    locale='fr'
                    dateFormat="dd/MM/yyyy"
                    selected={publicationDate}
                    onChange={(date) => {
                        setFormErrors(prev => ({ ...prev, publicationDate: null }));
                        setPublicationDate(date);
                    }}
                    placeholderText="jj/mm/aaaa"
                />

                {formErrors.publicationDate && (
                    <div className="invalid-feedback d-block">
                        {formErrors.publicationDate}
                    </div>
                )}

                <strong className='ms-5'>Mode kiosk :</strong>
                <ToggleButtonGroup type="radio" name="isKioskMode" onChange={setIsKioskMode}
                    value={isKioskMode} className='ms-2'>
                    <ToggleButton value={false} checked={!isKioskMode}
                        key="kiosk_mode_off" id="kiosk_mode_off">Non</ToggleButton>
                    <ToggleButton value={true} checked={!!isKioskMode}
                        key="kiosk_mode_on" id="kiosk_mode_on">Oui</ToggleButton>
                </ToggleButtonGroup>
                <br />

                <Row className="d-flex align-items-start pe-0 mt-5">

                    <Col md={6} lg={6} xxl={4}>
                        <label className="label-select mb-2">
                            Type de casques :
                        </label>
                        <span className="d-flex flex-column w-100">
                            <SelectMultipleItemsComponent
                                itemsSelected={deviceTypes}
                                setItemsSelected={setDeviceTypes}
                                items={allHeadsetsTypes}
                                name="deviceType"
                                itemKey="reference"
                                itemValue="label"
                                id='select-deviceType'
                            />
                        </span>

                        {formErrors.deviceTypes && (
                            <div className="invalid-feedback d-block">
                                {formErrors.deviceTypes}
                            </div>
                        )}
                    </Col>

                    <Col md={6} lg={6} xxl={4}>
                        <label className="label-select mb-2">
                            Loader conditions :
                        </label>

                        <table className="table table-sm table-bordered bg-white table-striped">
                            <thead>
                                <tr>
                                    <th>Type de casque</th>
                                    <th>Loader min</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deviceTypes.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="text-center text-muted">
                                            Aucun type sélectionné
                                        </td>
                                    </tr>
                                )}

                                {deviceTypes?.map(device => {
                                    const condition = loaderConditions?.find(
                                        c => c.deviceType === device.reference
                                    );

                                    return (
                                        <tr key={device.reference}>
                                            <td>{device.label}</td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    className='w-auto'
                                                    min={0}
                                                    step={1}
                                                    isInvalid={!!formErrors.loaderConditions[device.reference]}
                                                    value={condition?.loaderMinVersion ?? ''}
                                                    onChange={(e) =>
                                                        handleLoaderMinChange(
                                                            device.reference,
                                                            e.target.value
                                                        )
                                                    }
                                                />

                                                {formErrors.loaderConditions[device.reference] && (
                                                    <div className="invalid-feedback d-block">
                                                        {formErrors.loaderConditions[device.reference]}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Col>

                </Row>

                {/* CSRF */}
                <Form.Control
                    type="hidden"
                    name="token"
                    defaultValue={token}
                />
            </Form>

            <ButtonLoaderComponent
                onClick={submitForm}
                isSending={isSending}
                disabled={isSending}
                msg="Valider"
                variant="success"
            />
        </div>
    );
});

export default ApkFormComponent;
